import { c as _c } from "react/compiler-runtime";
import { MetaDot } from "@/components/ui/meta-dot";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCheckOutline18, IconCircleCheckOutline18, IconCircleXmarkOutline18, IconClockOutline18, IconCopyOutline18, IconMediaSkipToEndOutline18 } from "nucleo-ui-outline-18";

/**
 * NodeExecutionDetails Component
 *
 * Displays an "Inspect" link for viewing execution input/output data.
 * Clicking opens a tabbed modal with Input, Output, and Error tabs.
 */

import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { ErrorModalFooter } from "@/components/errors/ErrorModalFooter";
import { FailedNodeErrorSection } from "@/components/errors/FailedNodeErrorSection";
import { UnifiedErrorPanel } from "@/components/errors/UnifiedErrorPanel";
import { Badge } from "@/components/ui/badge";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { FixWithAIErrorContext } from "@/hooks/useFixWithAI";
import { useInternalAlertActor } from "@/hooks/useInternalAlertActor";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { sendInternalAlert } from "@/lib/sendInternalAlert";
import { openSupport } from "@/lib/support";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { cn } from "@/lib/utils";
import type { NodeExecutionState, NodeExecutionStatus } from "@/types/runStream";
import { useColorMode } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
export interface NodeInfo {
  name?: string;
  type?: string;
  description?: string;
  category?: "action" | "trigger" | "logic";
}
interface NodeExecutionDetailsProps {
  nodeState: NodeExecutionState | undefined;
  nodeInfo?: NodeInfo;
  className?: string;
  /** Called when "Fix with AI" button is clicked in error modal */
  onFixWithAI?: (errorCtx?: FixWithAIErrorContext) => void;
  workflowId?: string;
  runId?: string;
}

/**
 * Format a timestamp to a readable time string
 */
function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) return "—";
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  } catch {
    return timestamp;
  }
}

/**
 * Calculate duration between two timestamps
 */
function calculateDuration(start: string | undefined, end: string | undefined): string {
  if (!start || !end) return "—";
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate.getTime() - startDate.getTime();
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  } catch {
    return "—";
  }
}

/**
 * Get status display info
 */
function getStatusDisplay(status: NodeExecutionStatus): {
  label: string;
  icon: typeof IconCircleCheckOutline18;
  colorClass: string;
} {
  switch (status) {
    case "success":
      return {
        label: "Success",
        icon: IconCircleCheckOutline18,
        colorClass: "text-green-600 dark:text-green-400"
      };
    case "failure":
      return {
        label: "Failed",
        icon: IconCircleXmarkOutline18,
        colorClass: "text-red-600 dark:text-red-400"
      };
    case "skipped":
      return {
        label: "Skipped",
        icon: IconMediaSkipToEndOutline18,
        colorClass: "text-ds-text-muted"
      };
    case "running":
      return {
        label: "Running",
        icon: IconClockOutline18,
        colorClass: "text-blue-600 dark:text-blue-400"
      };
    case "waiting":
      return {
        label: "Waiting",
        icon: IconClockOutline18,
        colorClass: "text-amber-600 dark:text-amber-400"
      };
    default:
      return {
        label: "Skipped",
        icon: IconMediaSkipToEndOutline18,
        colorClass: "text-ds-text-disabled dark:text-ds-text-muted"
      };
  }
}

/**
 * Copy button component
 */
function CopyButton(t0) {
  const $ = _c(10);
  const {
    text,
    className
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== text) {
    t1 = async e => {
      e.stopPropagation();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("hover:bg-ds-neutral-200 dark:hover:bg-ds-neutral-700 rounded-md p-2 transition-colors duration-150", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== copied) {
    t3 = copied ? <IconCheckOutline18 className="h-4 w-4 text-green-500" /> : <IconCopyOutline18 className="text-ds-text-muted h-4 w-4" />;
    $[4] = copied;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handleCopy || $[7] !== t2 || $[8] !== t3) {
    t4 = <button onClick={handleCopy} className={t2} title="Copy to clipboard">{t3}</button>;
    $[6] = handleCopy;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function ExecutionMeta(t0) {
  const $ = _c(23);
  const {
    startedAt,
    finishedAt,
    runId
  } = t0;
  const hasStartedAt = Boolean(startedAt);
  const hasDuration = Boolean(startedAt && finishedAt);
  const hasRunId = Boolean(runId);
  if (!hasStartedAt && !hasDuration && !hasRunId) {
    return null;
  }
  let t1;
  if ($[0] !== hasStartedAt || $[1] !== startedAt) {
    t1 = hasStartedAt ? <span>Started {formatTimestamp(startedAt)}</span> : null;
    $[0] = hasStartedAt;
    $[1] = startedAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hasDuration || $[4] !== hasStartedAt) {
    t2 = hasStartedAt && hasDuration ? <MetaDot /> : null;
    $[3] = hasDuration;
    $[4] = hasStartedAt;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== finishedAt || $[7] !== hasDuration || $[8] !== startedAt) {
    t3 = hasDuration ? <span>Duration {calculateDuration(startedAt, finishedAt)}</span> : null;
    $[6] = finishedAt;
    $[7] = hasDuration;
    $[8] = startedAt;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== hasDuration || $[11] !== hasRunId || $[12] !== hasStartedAt) {
    t4 = (hasStartedAt || hasDuration) && hasRunId ? <MetaDot /> : null;
    $[10] = hasDuration;
    $[11] = hasRunId;
    $[12] = hasStartedAt;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== hasRunId || $[15] !== runId) {
    t5 = hasRunId ? <Badge className="mono-xs dark:bg-ds-neutral-900/80 bg-white/80">{runId}</Badge> : null;
    $[14] = hasRunId;
    $[15] = runId;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = <div className="label label-xs mt-2 flex flex-wrap items-center gap-x-1 gap-y-1">{t1}{t2}{t3}{t4}{t5}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
}
function useErrorModalActions({
  workflowId,
  runId,
  nodeInfo,
  nodeState,
  onOpenChange,
  onFixWithAI
}: {
  workflowId?: string;
  runId?: string;
  nodeInfo?: NodeInfo;
  nodeState?: NodeExecutionState;
  onOpenChange: (open: boolean) => void;
  onFixWithAI?: (errorCtx?: FixWithAIErrorContext) => void;
}) {
  const router = useRouter();
  const {
    actorOrgId,
    actorOrgName,
    actorUserId,
    actorUsername
  } = useInternalAlertActor();
  const handleGetSupport = useCallback(() => {
    void sendInternalAlert({
      type: "get_support_clicked",
      source: "node_error_modal",
      workflowId,
      runId,
      failedNodeName: nodeInfo?.name,
      failedNodeType: nodeInfo?.type,
      errorMessage: nodeState?.error,
      actorUserId,
      actorUsername,
      actorOrgId,
      actorOrgName
    });
    onOpenChange(false);
    if (workflowId && runId) {
      router.push(`/workflows/edit?id=${workflowId}&view=history&runId=${runId}`);
    }
    const nodeLabel = nodeInfo?.name || nodeInfo?.type;
    const subject = nodeLabel ? `Help with step "${nodeLabel}"` : "Help with a workflow step";
    const text = [runId ? `Run ID: ${runId}` : null, nodeState?.error ? `Error: ${nodeState.error}` : null].filter(Boolean).join("\n");
    setTimeout(() => {
      openSupport({
        subject,
        text: text || undefined
      });
    }, 100);
  }, [workflowId, runId, nodeInfo?.name, nodeInfo?.type, nodeState?.error, actorUserId, actorUsername, actorOrgId, actorOrgName, onOpenChange, router]);
  const handleFooterFixWithAI = useCallback(() => {
    if (!onFixWithAI) return;
    onOpenChange(false);
    setTimeout(() => {
      onFixWithAI({
        nodeName: nodeInfo?.name,
        nodeType: nodeInfo?.type,
        errorMessage: nodeState?.error
      });
    }, 50);
  }, [onFixWithAI, onOpenChange, nodeInfo?.name, nodeInfo?.type, nodeState?.error]);
  return {
    handleGetSupport,
    handleFooterFixWithAI
  };
}

/**
 * Error Detail Modal (exported for use elsewhere)
 * Now uses UnifiedErrorPanel for consistent error display.
 */
export function ErrorDetailModal(t0) {
  const $ = _c(37);
  const {
    open,
    onOpenChange,
    nodeInfo,
    nodeState,
    nodeConfig,
    onFixWithAI,
    onFieldUpdate,
    workflowId,
    runId,
    onRollback
  } = t0;
  let t1;
  if ($[0] !== onFixWithAI || $[1] !== onOpenChange) {
    t1 = onFixWithAI ? errorCtx => {
      onOpenChange(false);
      setTimeout(() => onFixWithAI(errorCtx), 50);
    } : undefined;
    $[0] = onFixWithAI;
    $[1] = onOpenChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFixWithAI = t1;
  let t2;
  if ($[3] !== onOpenChange || $[4] !== onRollback) {
    t2 = onRollback ? targetVersion => {
      onOpenChange(false);
      setTimeout(() => onRollback(targetVersion), 50);
    } : undefined;
    $[3] = onOpenChange;
    $[4] = onRollback;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleRollback = t2;
  let t3;
  if ($[6] !== nodeInfo || $[7] !== nodeState || $[8] !== onFixWithAI || $[9] !== onOpenChange || $[10] !== runId || $[11] !== workflowId) {
    t3 = {
      workflowId,
      runId,
      nodeInfo,
      nodeState,
      onOpenChange,
      onFixWithAI
    };
    $[6] = nodeInfo;
    $[7] = nodeState;
    $[8] = onFixWithAI;
    $[9] = onOpenChange;
    $[10] = runId;
    $[11] = workflowId;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    handleGetSupport,
    handleFooterFixWithAI
  } = useErrorModalActions(t3);
  const t4 = nodeState?.startedAt;
  const t5 = nodeState?.finishedAt;
  let t6;
  if ($[13] !== runId || $[14] !== t4 || $[15] !== t5) {
    t6 = <ExecutionMeta startedAt={t4} finishedAt={t5} runId={runId} />;
    $[13] = runId;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== handleFixWithAI || $[18] !== handleRollback || $[19] !== nodeConfig || $[20] !== nodeInfo || $[21] !== nodeState || $[22] !== onFieldUpdate || $[23] !== runId || $[24] !== t6 || $[25] !== workflowId) {
    t7 = <div className="bg-ds-surface-card max-h-[60vh] overflow-auto"><FailedNodeErrorSection nodeInfo={nodeInfo} nodeState={nodeState} nodeConfig={nodeConfig} workflowId={workflowId} runId={runId} variant="modal" showFixWithAI={false} showGetSupport={false} onFixWithAI={handleFixWithAI} onFieldUpdate={onFieldUpdate} onRollback={handleRollback} headerClassName="px-5 py-5 pb-0" bodyClassName="p-5" meta={t6} /></div>;
    $[17] = handleFixWithAI;
    $[18] = handleRollback;
    $[19] = nodeConfig;
    $[20] = nodeInfo;
    $[21] = nodeState;
    $[22] = onFieldUpdate;
    $[23] = runId;
    $[24] = t6;
    $[25] = workflowId;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = onFixWithAI ? handleFooterFixWithAI : undefined;
  let t9;
  if ($[27] !== handleGetSupport || $[28] !== t8) {
    t9 = <ErrorModalFooter onGetSupport={handleGetSupport} onFixWithCaddie={t8} />;
    $[27] = handleGetSupport;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t7 || $[31] !== t9) {
    t10 = <div className="flex flex-col overflow-hidden rounded-xl">{t7}{t9}</div>;
    $[30] = t7;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== onOpenChange || $[34] !== open || $[35] !== t10) {
    t11 = <ResponsiveModal open={open} onOpenChange={onOpenChange} title="Error Details" contentClass="max-w-2xl overflow-hidden rounded-xl p-0">{t10}</ResponsiveModal>;
    $[33] = onOpenChange;
    $[34] = open;
    $[35] = t10;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}

/**
 * Tabbed Inspect Modal for Input/Output/Error
 */
function InspectModal(t0) {
  const $ = _c(77);
  const {
    open,
    onOpenChange,
    nodeInfo,
    nodeState,
    onFixWithAI,
    defaultTab: t1,
    workflowId,
    runId
  } = t0;
  const defaultTab = t1 === undefined ? "output" : t1;
  const {
    colorMode
  } = useColorMode();
  const isDark = colorMode === "dark";
  let t2;
  if ($[0] !== isDark) {
    t2 = getSyntaxHighlightingStyle(isDark);
    $[0] = isDark;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const syntaxStyle = t2;
  const nodeVisual = useResolvedNodeVisual(nodeInfo?.type);
  const hasInput = nodeState?.input && Object.keys(nodeState.input).length > 0;
  const hasResult = nodeState?.result && Object.keys(nodeState.result).length > 0;
  const hasError = !!nodeState?.error;
  const [activeTab, setActiveTab] = useState(hasError ? "error" : defaultTab);
  const [prevOpen, setPrevOpen] = useState(false);
  if (open && !prevOpen) {
    setPrevOpen(true);
    setActiveTab(hasError ? "error" : defaultTab);
  }
  if (!open && prevOpen) {
    setPrevOpen(false);
  }
  let t3;
  if ($[2] !== nodeState) {
    t3 = !nodeState?.input || Object.keys(nodeState.input).length === 0 ? "{}" : JSON.stringify(nodeState.input, null, 2);
    $[2] = nodeState;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const inputJson = t3;
  let t4;
  if ($[4] !== nodeState) {
    t4 = !nodeState?.result || Object.keys(nodeState.result).length === 0 ? "{}" : JSON.stringify(nodeState.result, null, 2);
    $[4] = nodeState;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const outputJson = t4;
  let t5;
  if ($[6] !== nodeState) {
    t5 = nodeState ? getStatusDisplay(nodeState.status) : null;
    $[6] = nodeState;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const statusDisplay = t5;
  const StatusIcon = statusDisplay?.icon;
  let t6;
  if ($[8] !== nodeInfo || $[9] !== nodeState || $[10] !== onFixWithAI || $[11] !== onOpenChange || $[12] !== runId || $[13] !== workflowId) {
    t6 = {
      workflowId,
      runId,
      nodeInfo,
      nodeState,
      onOpenChange,
      onFixWithAI
    };
    $[8] = nodeInfo;
    $[9] = nodeState;
    $[10] = onFixWithAI;
    $[11] = onOpenChange;
    $[12] = runId;
    $[13] = workflowId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    handleGetSupport,
    handleFooterFixWithAI
  } = useErrorModalActions(t6);
  let t7;
  if ($[15] !== nodeInfo?.category) {
    t7 = () => {
      switch (nodeInfo?.category) {
        case "trigger":
          {
            return "#2450FF";
          }
        case "action":
          {
            return "#22c55e";
          }
        case "logic":
          {
            return "#8b5cf6";
          }
        default:
          {
            return "#22c55e";
          }
      }
    };
    $[15] = nodeInfo?.category;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const getIconColor = t7;
  let t8;
  if ($[17] !== StatusIcon || $[18] !== getIconColor || $[19] !== nodeInfo || $[20] !== nodeState?.finishedAt || $[21] !== nodeState?.startedAt || $[22] !== nodeState?.status || $[23] !== nodeVisual || $[24] !== runId || $[25] !== statusDisplay) {
    t8 = nodeInfo && <div className="px-5 py-5 pb-0"><div className="flex items-start gap-4"><NodeIconTile size="md" id={nodeInfo.type || "action"} subject={nodeInfo.name || nodeInfo.type || "Node"} context={nodeInfo.description || ""} logoUrl={nodeVisual.logoUrl} connector={nodeVisual.connector} alt={nodeInfo.name || "Node"} color={getIconColor()} /><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><h4 className="truncate text-lg font-semibold tracking-tight text-zinc-950 dark:text-white">{nodeInfo.name || nodeInfo.type || "Node"}</h4>{statusDisplay && StatusIcon && <Badge variant={nodeState?.status === "success" ? "success" : nodeState?.status === "failure" ? "destructive" : nodeState?.status === "running" ? "info" : nodeState?.status === "waiting" ? "warning" : "outline"}><StatusIcon className="h-3.5 w-3.5" />{statusDisplay.label}</Badge>}</div><ExecutionMeta startedAt={nodeState?.startedAt} finishedAt={nodeState?.finishedAt} runId={runId} /></div></div></div>;
    $[17] = StatusIcon;
    $[18] = getIconColor;
    $[19] = nodeInfo;
    $[20] = nodeState?.finishedAt;
    $[21] = nodeState?.startedAt;
    $[22] = nodeState?.status;
    $[23] = nodeVisual;
    $[24] = runId;
    $[25] = statusDisplay;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== hasInput) {
    t9 = hasInput && <TabsTrigger value="input">Input</TabsTrigger>;
    $[27] = hasInput;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== hasResult) {
    t10 = hasResult && <TabsTrigger value="output">Output</TabsTrigger>;
    $[29] = hasResult;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== hasError) {
    t11 = hasError && <TabsTrigger value="error">Error</TabsTrigger>;
    $[31] = hasError;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t9) {
    t12 = <TabsList className="justify-start">{t9}{t10}{t11}</TabsList>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t9;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== activeTab || $[38] !== inputJson || $[39] !== outputJson) {
    t13 = activeTab !== "error" && <div className="absolute right-4 top-1/2 -translate-y-1/2"><CopyButton text={activeTab === "input" ? inputJson : outputJson} /></div>;
    $[37] = activeTab;
    $[38] = inputJson;
    $[39] = outputJson;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t12 || $[42] !== t13) {
    t14 = <div className="border-ds-border-default relative border-b px-4">{t12}{t13}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      margin: 0,
      padding: "1rem",
      fontSize: "0.8125rem",
      lineHeight: "1.6",
      background: "transparent"
    };
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== inputJson || $[46] !== syntaxStyle) {
    t16 = <TabsContent value="input" className="mt-0 max-h-[50vh] overflow-auto"><SyntaxHighlighter language="json" style={syntaxStyle} customStyle={t15} showLineNumbers={true} wrapLines={true}>{inputJson}</SyntaxHighlighter></TabsContent>;
    $[45] = inputJson;
    $[46] = syntaxStyle;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      margin: 0,
      padding: "1rem",
      fontSize: "0.8125rem",
      lineHeight: "1.6",
      background: "transparent"
    };
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== outputJson || $[50] !== syntaxStyle) {
    t18 = <TabsContent value="output" className="mt-0 max-h-[50vh] overflow-auto"><SyntaxHighlighter language="json" style={syntaxStyle} customStyle={t17} showLineNumbers={true} wrapLines={true}>{outputJson}</SyntaxHighlighter></TabsContent>;
    $[49] = outputJson;
    $[50] = syntaxStyle;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== hasError || $[53] !== nodeInfo || $[54] !== nodeState || $[55] !== runId || $[56] !== workflowId) {
    t19 = hasError && <TabsContent value="error" className="mt-0 max-h-[50vh] overflow-auto"><div className="p-4"><UnifiedErrorPanel error={nodeState?.error || ""} nodeState={nodeState} nodeInfo={nodeInfo} workflowId={workflowId} runId={runId} variant="inline" showFixWithAI={false} onFixWithAI={undefined} /></div></TabsContent>;
    $[52] = hasError;
    $[53] = nodeInfo;
    $[54] = nodeState;
    $[55] = runId;
    $[56] = workflowId;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== activeTab || $[59] !== t14 || $[60] !== t16 || $[61] !== t18 || $[62] !== t19) {
    t20 = <Tabs value={activeTab} onValueChange={setActiveTab} variant="ghost" size="sm" className="flex-1">{t14}{t16}{t18}{t19}</Tabs>;
    $[58] = activeTab;
    $[59] = t14;
    $[60] = t16;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== handleFooterFixWithAI || $[65] !== handleGetSupport || $[66] !== hasError || $[67] !== onFixWithAI) {
    t21 = hasError ? <ErrorModalFooter onGetSupport={handleGetSupport} onFixWithCaddie={onFixWithAI ? handleFooterFixWithAI : undefined} /> : null;
    $[64] = handleFooterFixWithAI;
    $[65] = handleGetSupport;
    $[66] = hasError;
    $[67] = onFixWithAI;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== t20 || $[70] !== t21 || $[71] !== t8) {
    t22 = <div className="flex flex-col overflow-hidden rounded-xl">{t8}{t20}{t21}</div>;
    $[69] = t20;
    $[70] = t21;
    $[71] = t8;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== onOpenChange || $[74] !== open || $[75] !== t22) {
    t23 = <ResponsiveModal open={open} onOpenChange={onOpenChange} title="Inspect Execution" contentClass="max-w-2xl overflow-hidden rounded-xl p-0">{t22}</ResponsiveModal>;
    $[73] = onOpenChange;
    $[74] = open;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  return t23;
}
export interface NodeExecutionDetailsHandle {
  openErrorModal: () => void;
  openInspectModal: () => void;
}
const NodeExecutionDetails = forwardRef<NodeExecutionDetailsHandle, NodeExecutionDetailsProps>(function NodeExecutionDetails(t0, ref) {
  const $ = _c(30);
  const {
    nodeState,
    nodeInfo,
    className,
    onFixWithAI,
    workflowId,
    runId
  } = t0;
  const [inspectModalOpen, setInspectModalOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState("output");
  nodeState?.input;
  let t1;
  t1 = nodeState?.input && Object.keys(nodeState.input).length > 0;
  const hasInput = t1;
  nodeState?.result;
  const hasResult = (() => nodeState?.result && Object.keys(nodeState.result).length > 0)();
  const hasError = !!nodeState?.error;
  let t2;
  if ($[0] !== hasError) {
    t2 = () => ({
      openErrorModal: () => {
        setDefaultTab("error");
        setInspectModalOpen(true);
      },
      openInspectModal: () => {
        setDefaultTab(hasError ? "error" : "output");
        setInspectModalOpen(true);
      }
    });
    $[0] = hasError;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useImperativeHandle(ref, t2);
  if (!nodeState || !hasInput && !hasResult && !hasError) {
    return null;
  }
  let t3;
  if ($[2] !== hasError) {
    t3 = e => {
      e.stopPropagation();
      setDefaultTab(hasError ? "error" : "output");
      setInspectModalOpen(true);
    };
    $[2] = hasError;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpenInspect = t3;
  let t4;
  if ($[4] !== defaultTab || $[5] !== inspectModalOpen || $[6] !== nodeInfo || $[7] !== nodeState || $[8] !== onFixWithAI || $[9] !== runId || $[10] !== workflowId) {
    t4 = <InspectModal open={inspectModalOpen} onOpenChange={setInspectModalOpen} nodeInfo={nodeInfo} nodeState={nodeState} onFixWithAI={onFixWithAI} defaultTab={defaultTab} workflowId={workflowId} runId={runId} />;
    $[4] = defaultTab;
    $[5] = inspectModalOpen;
    $[6] = nodeInfo;
    $[7] = nodeState;
    $[8] = onFixWithAI;
    $[9] = runId;
    $[10] = workflowId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== className) {
    t5 = cn("border-ds-neutral-200 dark:border-ds-neutral-700/50 border-t pt-2", className);
    $[12] = className;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = hasError ? "text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" : "text-ds-text-muted hover:text-blue-500 dark:hover:text-blue-400";
  let t7;
  if ($[14] !== t6) {
    t7 = cn("label label-xs flex w-full items-center justify-center gap-1.5 py-1 transition-colors", t6);
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IconMagnifyingGlass className="h-3 w-3" />;
    t9 = <span className="font-medium">Inspect</span>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[18] !== hasError) {
    t10 = hasError && <Badge variant="destructive" className="micro-label ml-1 px-1.5">Error</Badge>;
    $[18] = hasError;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== handleOpenInspect || $[21] !== t10 || $[22] !== t7) {
    t11 = <button onClick={handleOpenInspect} className={t7}>{t8}{t9}{t10}</button>;
    $[20] = handleOpenInspect;
    $[21] = t10;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t5) {
    t12 = <div className={t5}>{t11}</div>;
    $[24] = t11;
    $[25] = t5;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t4) {
    t13 = <>{t4}{t12}</>;
    $[27] = t12;
    $[28] = t4;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
});
export default NodeExecutionDetails;